import { AppContext } from '@application/context/AppContext';
import { isRemoteMultiStationMode } from '@infra/licence/DataModeResolver';
import { getBusinessApiClient, BusinessApiConflictError } from '@infra/api/BusinessApiClient';
import { RemoteSyncStore } from '@infra/sync/RemoteSyncStore';
import { SqliteEntityBridge } from '@infra/persistence/SqliteEntityBridge';
import { logger } from '@infra/telemetry/Logger';
import { AuthError } from '@infra/auth/AuthService';
import {
  wrapModuleDataPayload,
  unwrapModuleDataPayload,
} from '@application/persistence/moduleDataPayload';
import { canWriteModuleData } from '@application/persistence/moduleDataWriteRoles';
import { assertLicenceAllowsWrite } from '@infra/tenancy/entityWriteGuard';

const TABLE = 'module_data' as const;
const REMOTE_RESOURCE = 'module-data';

export interface ModuleDataRow {
  id: string;
  tenant_id: string;
  namespace: string;
  value: unknown;
}

function rowId(tenantId: string, namespace: string): string {
  return `${tenantId}::${namespace}`;
}

/** Clé localStorage de repli quand SQLite n’est pas le moteur primaire (A-01 / A-02). */
function defaultLegacyKey(tenantId: string, namespace: string): string {
  return `nexxus.module.${tenantId}.${namespace}`;
}

/**
 * Clé localStorage de repli — toujours isolée par tenant.
 * Évite le mélange cabinet (ex. `nexxus.rh.conges` / `nexxus.fiscalite.dts` partagés).
 * Exception : scope appareil `__device__` (libellé poste).
 */
function resolveLegacyKey(tenantId: string, namespace: string, legacyKey?: string): string {
  if (!legacyKey || legacyKey.length === 0) {
    return defaultLegacyKey(tenantId, namespace);
  }
  if (tenantId === '__device__' || legacyKey.includes(tenantId)) {
    return legacyKey;
  }
  return `${legacyKey}.${tenantId}`;
}

/** Exporté pour tests / migration one-shot des anciennes clés globales. */
export function moduleDataLegacyKey(
  tenantId: string,
  namespace: string,
  legacyKey?: string,
): string {
  return resolveLegacyKey(tenantId, namespace, legacyKey);
}

function assertCanWriteNamespace(namespace: string): void {
  const role = AppContext.getInstance().auth.getSession()?.role;
  if (!role) return;
  if (!canWriteModuleData(role, namespace)) {
    throw new AuthError('INSUFFICIENT_ROLE');
  }
}

async function readLocal<T>(
  tenantId: string,
  namespace: string,
  fallback: T,
  legacyKey?: string,
): Promise<T> {
  const key = resolveLegacyKey(tenantId, namespace, legacyKey);
  if (!(await SqliteEntityBridge.isSqlitePrimary())) {
    try {
      const raw = localStorage.getItem(key);
      if (!raw) return fallback;
      return JSON.parse(raw) as T;
    } catch {
      return fallback;
    }
  }
  const row = await SqliteEntityBridge.get<ModuleDataRow>(TABLE, rowId(tenantId, namespace));
  if (!row) {
    // Repli : données saisies avant la bascule SQLite
    try {
      const raw = localStorage.getItem(key);
      if (raw) return JSON.parse(raw) as T;
    } catch {
      /* ignore */
    }
    return fallback;
  }
  return (row.value as T) ?? fallback;
}

async function writeLocal(
  tenantId: string,
  namespace: string,
  value: unknown,
  legacyKey?: string,
): Promise<void> {
  const key = resolveLegacyKey(tenantId, namespace, legacyKey);
  if (!(await SqliteEntityBridge.isSqlitePrimary())) {
    localStorage.setItem(key, JSON.stringify(value));
    return;
  }
  const row: ModuleDataRow = {
    id: rowId(tenantId, namespace),
    tenant_id: tenantId,
    namespace,
    value,
  };
  await SqliteEntityBridge.put(TABLE, row);
  // Miroir localStorage pour export/import Dexie et bascule
  try {
    localStorage.setItem(key, JSON.stringify(value));
  } catch {
    /* quota / private mode — non bloquant */
  }
}

/** Données module tenant-scopées avec sync distant + cache offline. */
export class ModuleDataSyncService {
  static isAvailable(): boolean {
    return SqliteEntityBridge.isAvailable();
  }

  static async getJson<T>(
    tenantId: string,
    namespace: string,
    fallback: T,
    legacyKey?: string,
  ): Promise<T> {
    const ctx = AppContext.getInstance();
    const mode = ctx.getLockedDataMode?.() ?? null;
    if (!isRemoteMultiStationMode(mode)) {
      return readLocal(tenantId, namespace, fallback, legacyKey);
    }

    const api = getBusinessApiClient();
    const online = (await api.confirmWriteReady()) && Boolean(api.hasUserSession());

    if (online) {
      try {
        const remote = await api.getModuleData(namespace);
        if (remote) {
          const value = unwrapModuleDataPayload(remote.payload) as T;
          if (await RemoteSyncStore.isEnabled()) {
            await RemoteSyncStore.putCache(
              tenantId,
              REMOTE_RESOURCE,
              namespace,
              remote.payload,
              remote.version,
              false,
            );
          }
          await writeLocal(tenantId, namespace, value, legacyKey);
          return value;
        }
      } catch (err) {
        logger.warn({
          event: 'module_data_remote_fallback',
          namespace,
          tenantId,
          error: String(err),
        });
      }
    }

    const cached = (await RemoteSyncStore.getCache(tenantId, REMOTE_RESOURCE, namespace)) as
      (Record<string, unknown> & { _remoteVersion?: number }) | null;
    if (cached) return unwrapModuleDataPayload(cached) as T;

    return readLocal(tenantId, namespace, fallback, legacyKey);
  }

  static async setJson(
    tenantId: string,
    namespace: string,
    value: unknown,
    legacyKey?: string,
  ): Promise<void> {
    assertLicenceAllowsWrite();
    assertCanWriteNamespace(namespace);
    const ctx = AppContext.getInstance();
    const mode = ctx.getLockedDataMode?.() ?? null;
    if (!isRemoteMultiStationMode(mode)) {
      await writeLocal(tenantId, namespace, value, legacyKey);
      return;
    }

    const payload = wrapModuleDataPayload(value);

    const api = getBusinessApiClient();
    const cached = (await RemoteSyncStore.getCache(tenantId, REMOTE_RESOURCE, namespace)) as {
      _remoteVersion?: number;
    } | null;
    const online = (await api.confirmWriteReady()) && Boolean(api.hasUserSession());
    // Navigateur Cloud : pas de cache de synchronisation, donc aucune version connue. On relit la version
    // courante du serveur avant d'écrire (sinon toute écriture après la première part avec la version 1 et
    // reçoit « Conflit de version »).
    let version = cached?._remoteVersion ?? 1;
    if (online && cached?._remoteVersion === undefined) {
      try {
        version = (await api.getModuleData(namespace))?.version ?? 1;
      } catch {
        version = 1;
      }
    }

    if (online) {
      try {
        const saved = await api.upsertModuleData(namespace, payload, version);
        if (await RemoteSyncStore.isEnabled()) {
          await RemoteSyncStore.putCache(
            tenantId,
            REMOTE_RESOURCE,
            namespace,
            saved.payload,
            saved.version,
            false,
          );
        }
        await writeLocal(tenantId, namespace, unwrapModuleDataPayload(saved.payload), legacyKey);
        return;
      } catch (err) {
        if (!(err instanceof BusinessApiConflictError)) throw err;
      }
    }

    if (!(await RemoteSyncStore.isEnabled()) || !api.hasUserSession()) {
      await writeLocal(tenantId, namespace, value, legacyKey);
      return;
    }

    await RemoteSyncStore.putCache(tenantId, REMOTE_RESOURCE, namespace, payload, version, true);
    await RemoteSyncStore.enqueue({
      tenant_id: tenantId,
      resource: REMOTE_RESOURCE,
      action: 'update',
      entity_id: namespace,
      payload,
      base_version: version,
    });
    await writeLocal(tenantId, namespace, value, legacyKey);
    logger.info({ event: 'module_data_queued_offline', namespace, tenantId });
  }

  static async migrateLegacyKey(
    tenantId: string,
    namespace: string,
    legacyKey: string,
  ): Promise<boolean> {
    if (!(await SqliteEntityBridge.isSqlitePrimary())) return false;
    const raw = localStorage.getItem(legacyKey);
    if (!raw) return false;
    try {
      const value = JSON.parse(raw) as unknown;
      await ModuleDataSyncService.setJson(tenantId, namespace, value, legacyKey);
      localStorage.removeItem(legacyKey);
      logger.info({ event: 'module_data_migrated', namespace, tenantId });
      return true;
    } catch {
      return false;
    }
  }

  /** Ingestion d’un namespace déjà lu sur l’API (hydratation Cloud, sans re-GET). */
  static async ingestRemote(
    tenantId: string,
    remote: {
      namespace: string;
      payload: Record<string, unknown>;
      version: number;
    },
    legacyKey?: string,
  ): Promise<void> {
    const value = unwrapModuleDataPayload(remote.payload);
    if (await RemoteSyncStore.isEnabled()) {
      await RemoteSyncStore.putCache(
        tenantId,
        REMOTE_RESOURCE,
        remote.namespace,
        remote.payload,
        remote.version,
        false,
      );
    }
    await writeLocal(tenantId, remote.namespace, value, legacyKey);
  }

  static async listForTenant(tenantId: string): Promise<ModuleDataRow[]> {
    if (await SqliteEntityBridge.isSqlitePrimary()) {
      return SqliteEntityBridge.list<ModuleDataRow>(TABLE, tenantId);
    }
    // Mode Dexie / web : reconstituer depuis le miroir localStorage (A-22)
    const prefix = `nexxus.module.${tenantId}.`;
    const rows: ModuleDataRow[] = [];
    for (let i = 0; i < localStorage.length; i += 1) {
      const key = localStorage.key(i);
      if (!key || !key.startsWith(prefix)) continue;
      const namespace = key.slice(prefix.length);
      if (!namespace) continue;
      try {
        const raw = localStorage.getItem(key);
        if (raw == null) continue;
        rows.push({
          id: rowId(tenantId, namespace),
          tenant_id: tenantId,
          namespace,
          value: JSON.parse(raw) as unknown,
        });
      } catch {
        /* ignore corrupt */
      }
    }
    return rows;
  }
}
